import {client} from '@/utils/gql'
import {
  Document_Bool_Exp,
  Document_Insert_Input,
  Document_Order_By,
  Document_Select_Column,
  Document_Set_Input,
  DocumentFragment,
} from '@/utils/graphqlSdk'
import {NotFoundError, ServerError} from '@/utils/server/errors'

const getByIdOrNull = async (id: number): Promise<DocumentFragment | null> =>
  (await client.documentGetById({id})).document_by_pk || null

const getById = async (id: number): Promise<DocumentFragment> => {
  const document = await getByIdOrNull(id)

  if (!document) {
    throw new NotFoundError('Document does not exist', {id})
  }

  return document
}

const getMany = async (
  filter: Document_Bool_Exp,
  order_by?: Document_Order_By | Document_Order_By[],
  offset?: number,
  limit?: number,
  distinct_on?: Document_Select_Column[],
): Promise<DocumentFragment[]> => {
  return (await client.documentGetMany({filter, order_by, offset, limit, distinct_on})).document
}

const getOneOrNull = async (filter: Document_Bool_Exp): Promise<DocumentFragment | null> => {
  const document = (await client.documentGetMany({filter})).document

  if (document.length > 1) {
    throw new ServerError('Document getOneOrNull returned multiple results', {filter})
  }

  return document?.[0] ?? null
}

const getOne = async (filter: Document_Bool_Exp): Promise<DocumentFragment> => {
  const document = await getOneOrNull(filter)

  if (!document) {
    throw new NotFoundError('Get one returned null', {filter})
  }

  return document
}

const deleteOne = async (id: number): Promise<DocumentFragment> => {
  const document = (await client.documentDeleteOne({id})).delete_document_by_pk
  if (!document) {
    throw new ServerError('Delete document by id returned empty result', {id, document})
  }

  return document
}

const insertOne = async (object: Document_Insert_Input): Promise<DocumentFragment> => {
  const document = (await client.documentInsertOne({object})).insert_document_one
  if (!document) {
    throw new ServerError('Document insert returned null', {object})
  }

  return document
}

const updateOne = async (id: number, changes: Document_Set_Input): Promise<DocumentFragment> => {
  const result = (await client.documentUpdateOne({id, changes}))?.update_document_by_pk

  if (!result) {
    throw new ServerError('Document returned null after update', {id, changes})
  }

  return result
}

const insertMany = async (objects: Document_Insert_Input[]): Promise<DocumentFragment[]> => {
  const result = (await client.documentInsertMany({objects}))?.insert_document?.returning

  if (!result) {
    throw new ServerError('Document returned null after insert many', {objects})
  }

  return result
}

export const documentOrm = {
  getById,
  getByIdOrNull,
  getMany,
  getOneOrNull,
  getOne,
  deleteOne,
  insertOne,
  updateOne,
  insertMany,
}
